<docs>
A hover/focus tooltip wrapping floating-vue: the trigger is the default slot, the body is the
`content` prop or the `#content` slot. With neither, the component is renderless: the default slot
renders bare with no floating-vue wrapper at all, so a caller can always wrap and just leave the text
empty to switch the tooltip off. No v-model, no events. For a field's info-icon tooltip prefer the
`tooltip` prop on the field components (ZoneTextInput, ZoneLabel…) or
`<ZoneIcon icon="info" tooltip="…" />` rather than composing this directly.

## Notes

- floating-vue wraps the default slot in a `div.v-popper` and anchors the tooltip to that div, not to
  the trigger inside it, so the wrapper must hug the trigger: ZoneTooltip renders it `inline-block`
  for exactly that reason. A block wrapper stretches to its container and the tooltip centres on the
  container instead, possibly hundreds of px away from a small trigger. Pass `block` only when the
  trigger itself fills the width; a `w-full` trigger under the default `inline-block` shrinks to its
  content, which is what `block` is for.
- The popper is teleported out of the component subtree into `#zone-teleport` (carries `.w-tw`, so
  Zone styles apply even on legacy pages whose `body` lacks it), or into `body` where the page has
  no `#zone-teleport`. `container` overrides the selector. In tests assert the trigger; the popper is
  not inside `wrapper.html()`.
- `anchor` positions the popper against an element inside the trigger (a selector), while hovering
  anywhere on the trigger still opens it: a full-width row whose tooltip centres on its name.
</docs>

<template>
  <FloatingTooltip
    v-if="active"
    :class="block ? 'block' : 'inline-block'"
    :placement="placement"
    :delay="delay"
    :distance="offset"
    :disabled="disabled"
    :instant-move="instantMove"
    :hide-triggers="hideTriggersConfig"
    v-bind="popperBinding"
  >
    <slot />
    <template #popper>
      <!-- `w-max`: inside a zero-width container (`#zone-teleport`) shrink-to-fit would wrap every word. -->
      <div class="rounded bg-gray-800 px-3 py-1.5 w-max max-w-[368px] text-white text-sm whitespace-normal break-words text-center pointer-events-none">
        <slot name="content">{{ content }}</slot>
      </div>
    </template>
  </FloatingTooltip>
  <slot v-else />
</template>

<script lang="ts">
import { computed, type PropType } from 'vue';
import { Tooltip as FloatingTooltip } from 'floating-vue';
import 'floating-vue/dist/style.css';

export type TooltipPlacement = 'top' | 'right' | 'bottom' | 'left';

const PLACEMENTS: TooltipPlacement[] = ['top', 'right', 'bottom', 'left'];
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneTooltip' });

const props = defineProps({
  /**
   * Plain-text body. Empty or unset with no `#content` slot makes the component renderless: the
   * trigger renders bare.
   */
  content: {
    type: String,
    default: null,
  },
  /** Which side of the trigger the tooltip appears on. */
  placement: {
    type: String as PropType<TooltipPlacement>,
    default: 'top',
    validator: (v: string) => PLACEMENTS.includes(v as TooltipPlacement),
  },
  /**
   * Hover delay in ms before the tooltip shows; none by default. Pass `{ show, hide }` for separate
   * delays.
   */
  delay: {
    type: [Number, Object] as PropType<number | { show: number; hide: number }>,
    default: 0,
  },
  /** Gap in px between the tooltip and the trigger. */
  offset: {
    type: Number,
    default: undefined,
  },
  /** Disable the tooltip. */
  disabled: {
    type: Boolean,
    default: false,
  },
  /** When false, tooltip always waits the full delay instead of showing instantly when
   *  moving quickly between multiple tooltips. Mirrors Ruby's quick_hover: false. */
  /**
   * Show instantly when moving between adjacent tooltips. `false` always waits the full delay
   * (Ruby's `quick_hover: false`).
   */
  instantMove: {
    type: Boolean,
    default: true,
  },
  /** Clicking the trigger hides the tooltip until the next hover. */
  hideOnClick: {
    type: Boolean,
    default: false,
  },
  /** Render the reference wrapper as `display: block` instead of `inline-block`. Only for a
   *  trigger that is meant to fill its container's width — the wrapper IS the element the
   *  tooltip anchors to, so it must hug the trigger or the tooltip drifts off-target. */
  /**
   * Render the wrapper as `display: block` instead of `inline-block`. Only for a trigger meant to
   * fill its container's width: the wrapper is the element the tooltip anchors to, so it must hug
   * the trigger or the tooltip drifts off-target.
   */
  block: {
    type: Boolean,
    default: false,
  },
  /**
   * Selector the popper is appended to. Defaults to `#zone-teleport` (carries `.w-tw` and stacks
   * above other teleported Zone UI), or `body` when the page has none.
   */
  container: {
    type: String,
    default: null,
  },
  /**
   * CSS selector, inside the trigger, for the element the tooltip is positioned against. The whole
   * trigger still opens it on hover. Falls back to the wrapper when nothing matches.
   */
  anchor: {
    type: String,
    default: null,
  },
  /** Force the tooltip open (manual control, testing). Leave `false` for normal hover behaviour. */
  shown: {
    type: Boolean,
    default: false,
  },
});

const slots = defineSlots<{
  /** The trigger element(s) the tooltip anchors to. */
  default?(): any;
  /** The tooltip body when it needs markup; for plain text use the `content` prop. */
  content?(): any;
}>();

/** Whether there is anything to show; without it the wrapper (and its layout box) is skipped. */
const active = computed<boolean>(() => Boolean(props.content || slots.content));

// hideTriggers accepts floating-vue TriggerEvent values: 'hover' | 'focus' | 'click' | 'touch'
// When hideOnClick is false, return undefined so floating-vue uses its theme defaults.
const hideTriggersConfig = computed<((t: string[]) => string[]) | undefined>(() => {
  if (props.hideOnClick) {
    return (triggers) => [...triggers, 'click'];
  }

  return undefined;
});

// Only pass `shown` to floating-vue when explicitly true. Binding :shown="false"
// locks floating-vue's internal state to closed, preventing hover triggers from working.
// floating-vue throws when the `container` selector matches nothing, so `#zone-teleport` is only
// the default where the page has one; otherwise its own `body` default stays.
// `referenceNode` is called with the floating-vue Popper as `this`; its `$el` is the wrapper div.
const popperBinding = computed<{ shown?: true; container?: string; referenceNode?: () => Element }>(() => {
  const { anchor } = props;
  const container = props.container ?? (document.getElementById('zone-teleport') ? '#zone-teleport' : null);

  return {
    ...(props.shown ? { shown: true } : {}),
    ...(container ? { container } : {}),
    ...(anchor
      ? {
          referenceNode(this: { $el: Element }) {
            return this.$el.querySelector(anchor) ?? this.$el;
          },
        }
      : {}),
  };
});
</script>

<!--
  Global on purpose (the popper teleports out of this component's subtree): strips floating-vue's
  own tooltip theme — black/80 ground, 7/12px padding, 6px radius — so the surface is exactly
  Zone::Tooltip's `rounded px-3 py-1.5 bg-gray-800` div above, and paints the arrow gray-800 too.
-->
<style>
.v-popper--theme-tooltip .v-popper__inner {
  background: transparent;
  color: inherit;
  border-radius: 0;
  padding: 0;
}

.v-popper--theme-tooltip .v-popper__arrow-outer,
.v-popper--theme-tooltip .v-popper__arrow-inner {
  border-color: #1f2937; /* gray-800, the surface above */
}
</style>
